import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import {
  Save, ArrowLeft, Shield, Lock, Edit3, PlusSquare, Trash2,
  Package, ShoppingCart, Layout, Wrench, Users, MapPin, Gauge, Building2, Menu, Network, Workflow, BarChart, AlertCircle, UserCheck, FileCheck, Blocks, Scale, Settings, Search, ChevronLeft, ChevronRight
} from 'lucide-react';
import AuditHistoryModal from './AuditHistoryModal';
import { swalTheme, swalDangerColor } from '../lib/swalTheme';
import api from './axiosConfig';
import { resolveIcon } from './iconResolver';
import { useFontSize } from './FontSizeContext';

// 🎯 labelKey بيشاور على مفتاح ترجمة موجود بالفعل في i18n.js (نفس المفاتيح
// المستخدمة في الـ Sidebar لنفس الموديول) - عشان منعملش ترجمة مكررة لنفس
// اسم الموديول في مكانين مختلفين من الكود
const MODULE_GROUPS = [
  {
    groupKey: 'roles.groups.core_security',
    modules: [
      { id: 'users', labelKey: 'menu.user_list', icon: Lock },
      { id: 'roles', labelKey: 'roles.modules.roles_mgmt', icon: Shield },
      { id: 'dashboard', labelKey: 'roles.modules.dashboard', icon: BarChart },
    ]
  },
  {
    groupKey: 'roles.groups.inventory_supply',
    modules: [
      { id: 'items', labelKey: 'menu.item_master', icon: Package },
      { id: 'tools', labelKey: 'menu.tools', icon: Package },
      { id: 'inventory', labelKey: 'menu.balances', icon: Layout },
      { id: 'invusage', labelKey: 'menu.transfers', icon: Layout },
      { id: 'storerooms', labelKey: 'menu.storerooms', icon: MapPin },
      { id: 'receiving', labelKey: 'menu.receiving', icon: ShoppingCart },
    ]
  },
  {
    groupKey: 'roles.groups.purchasing',
    modules: [
      { id: 'pr', labelKey: 'menu.pr', icon: ShoppingCart },
      { id: 'po', labelKey: 'menu.po', icon: ShoppingCart },
      { id: 'rfq', labelKey: 'menu.rfq', icon: ShoppingCart },
      { id: 'mr', labelKey: 'apps.MR', icon: ShoppingCart },
      { id: 'invoices', labelKey: 'menu.invoices', icon: ShoppingCart },
      { id: 'vendors', labelKey: 'menu.vendors', icon: UserCheck },
    ]
  },
  {
    groupKey: 'roles.groups.work_assets',
    modules: [
      { id: 'assets', labelKey: 'menu.asset_registry', icon: Shield },
      { id: 'locations', labelKey: 'menu.locations', icon: MapPin },
      { id: 'workorders', labelKey: 'menu.wo_tracking', icon: Edit3 },
      { id: 'pm', labelKey: 'menu.pm_schedules', icon: Wrench },
      { id: 'jp', labelKey: 'menu.job_plans', icon: Wrench },
      { id: 'meters', labelKey: 'menu.meters', icon: Gauge },
      { id: 'asset-categories', labelKey: 'menu.categories', icon: Layout},
      { id: 'failure-codes', labelKey: 'menu.failure_codes', icon: Shield},
    ]
  },
  {
    groupKey: 'roles.groups.accounting',
    modules: [
      { id: 'gl-accounts', labelKey: 'menu.chart_of_accounts', icon: Scale },
    ]
  },
  {
    groupKey: 'roles.groups.contracts',
    modules: [
      { id: 'contracts', labelKey: 'menu.contracts_master', icon: FileCheck },
      { id: 'sla', labelKey: 'menu.sla', icon: FileCheck },
    ]
  },
  {
    groupKey: 'roles.groups.service_requests',
    modules: [
      { id: 'sr', labelKey: 'menu.service_requests', icon: ShoppingCart },
    ]
  },
  {
    groupKey: 'roles.groups.incidents',
    modules: [
      { id: 'incidents', labelKey: 'menu.incidents', icon: AlertCircle },
    ]
  },
  {
    groupKey: 'roles.groups.resources',
    modules: [
      { id: 'labor', labelKey: 'menu.labor', icon: Users },
      { id: 'craft', labelKey: 'menu.crafts', icon: Wrench },
      { id: 'person', labelKey: 'menu.person', icon: UserCheck },
    ]
  },
  {
    groupKey: 'roles.groups.administration',
    modules: [
      { id: 'organizations', labelKey: 'menu.organization', icon: Building2 },
      // 🎯 صلاحية مستقلة لإعدادات النظام (مؤسسة __GLOBAL__) - مش تبع صلاحية
      // المؤسسات، ومن غيرها محدش غير السوبر أدمن يقدر يفتحها أو يحفظ فيها
      { id: 'system-settings', labelKey: 'menu.system_settings', icon: Settings },
      { id: 'domains', labelKey: 'menu.domains', icon: Menu },
      { id: 'classifications', labelKey: 'menu.classifications', icon: Network },
      { id: 'cobiemanager', labelKey: 'menu.cobie', icon: Layout },
      { id: 'workflow', labelKey: 'menu.workflow', icon: Workflow },
      { id: 'uniimporter', labelKey: 'menu.importer', icon: PlusSquare },
      { id: 'admin/workflow/studio', labelKey: 'menu.scripts', icon: Workflow },
      { id: 'custom_apps', labelKey: 'menu.custom_app_builder', icon: Blocks },
    ]
  }
];

// 🎯 بارامتر groups بدل الاعتماد على MODULE_GROUPS الثابتة مباشرة، عشان
// نقدر نحسبها كمان لمجموعة التطبيقات المخصصة الديناميكية تحت
const generateDefaultPermissions = (groups) => {
  const perms = {};
  groups.forEach(group => {
    group.modules.forEach(mod => {
      perms[mod.id] = { read: false, create: false, update: false, delete: false };
    });
  });
  return perms;
};

const RoleDetails = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new' || id === 'undefined';
  const [loading, setLoading] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [activeTab, setActiveTab] = useState('group');
  // 🎯 تعديل/حذف الرولات مقفول على SUPER_ADMIN بس في الباك إند (لأن اللي
  // يقدر يعدّل رول يقدر يديله أي صلاحية حتى SUPER_ADMIN نفسها) - نعكس نفس
  // القيد هنا عشان الأزرار متتفعلش وتفشل بعد كده برسالة 403 من السيرفر
  // 🎯 fallback على اسم الرول لجلسات قديمة اتعملها login قبل ما is_super_admin
  // يترجع من الباك إند - وإلا سوبر أدمن حقيقي بجلسة قديمة (متسجلش دخول تاني
  // بعد التحديث) هيلاقي زرار الحفظ متقفل عليه غلط
  const currentUserRole = JSON.parse(localStorage.getItem('user') || '{}')?.role;
  const isSuperAdmin = currentUserRole?.is_super_admin === true || currentUserRole?.name === 'SUPER_ADMIN';

  // 🎯 التطبيقات المخصصة بتضاف كمجموعة صلاحيات ديناميكية - لازم تكون موجودة
  // في MODULE_GROUPS الفعلية اللي بترسم بيها الشاشة وفي الـ default permissions
  // قبل ما اليوزر يضغط أي checkbox، وإلا handlePermissionChange هيحاول يعدل
  // على مفتاح مش موجود ويوقع
  const [customModels, setCustomModels] = useState([]);
  useEffect(() => {
    api.get('/custom-models', { params: { published_only: false } })
      .then(res => setCustomModels(res.data || []))
      .catch(() => setCustomModels([]));
  }, []);

  const allModuleGroups = React.useMemo(() => {
    if (customModels.length === 0) return MODULE_GROUPS;
    return [
      ...MODULE_GROUPS,
      {
        groupKey: 'menu.custom_apps',
        modules: customModels.map(m => ({ id: m.module_key, label: m.label, icon: resolveIcon(m.icon_name) })),
      },
    ];
  }, [customModels]);

  const defaultPermissions = React.useMemo(() => generateDefaultPermissions(allModuleGroups), [allModuleGroups]);

  const [roleData, setRoleData] = useState({ name: '', permissions: defaultPermissions, all_sites: false, sites: [] });
  const [allSites, setAllSites] = useState([]);
  useEffect(() => {
    api.get('/site-access/all-sites').then(res => setAllSites(res.data || [])).catch(() => setAllSites([]));
  }, []);
  const availableSites = allSites.filter(s => !roleData.sites.some(r => r.site_id === s.site_id));

  // لو المودلز الكستم اتحملوا بعد ما الـ roleData اتظبط أصلاً (أو بعد ما
  // الرول الحالي اتحمل من الباك إند)، ضيف أي مفتاح جديد ناقص من غير ما تلمس
  // القيم الموجودة فعلاً
  useEffect(() => {
    setRoleData(prev => ({ ...prev, permissions: { ...defaultPermissions, ...prev.permissions } }));
  }, [defaultPermissions]);

  useEffect(() => {
    const openLog = () => setShowAudit(true);
    window.addEventListener('GLOBAL_LOG_HANDLER', openLog);
    return () => window.removeEventListener('GLOBAL_LOG_HANDLER', openLog);
  }, []);

  useEffect(() => {
    const fetchRole = async () => {
      if (isNew) return;
      setLoading(true);
      try {
        const res = await axios.get(`/api/roles/${id}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        // تأكد من معالجة الصلاحيات سواء كانت String أو Object
        let perms = typeof res.data.permissions === 'string' ? JSON.parse(res.data.permissions) : res.data.permissions;
        setRoleData({
            id: res.data.id,
            name: res.data.name || res.data.role_name,
            permissions: { ...defaultPermissions, ...perms },
            all_sites: !!res.data.all_sites,
            sites: res.data.sites || []
        });
      } catch (err) {
        toast.error(t('roles.details.loadFail'));
      } finally {
        setLoading(false);
      }
    };
    fetchRole();
  }, [id, isNew]);

  const handlePermissionChange = (modId, action) => {
    setRoleData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [modId]: { ...prev.permissions[modId], [action]: !prev.permissions[modId][action] }
      }
    }));
  };

  // دالة الـ Select All للمجموعة
  const toggleGroupAction = (modules, action) => {
    const allActive = modules.every(m => roleData.permissions[m.id][action]);
    const updatedPerms = { ...roleData.permissions };
    modules.forEach(m => {
      updatedPerms[m.id][action] = !allActive;
    });
    setRoleData({ ...roleData, permissions: updatedPerms });
  };

  const handleSave = async () => {
    if (!roleData.name) return toast.error(t('roles.details.nameRequired'));
    setLoading(true);
    try {
      // نرسل الصلاحيات كـ JSON string للباكيند
      const payload = {
        name: roleData.name,
        permissions: JSON.stringify(roleData.permissions),
        all_sites: roleData.all_sites,
        sites: roleData.sites.map(s => s.site_id)
      };
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };

      if (isNew) {
        await axios.post('/api/roles', payload, { headers });
      } else {
        await axios.put(`/api/roles/${id}`, payload, { headers });
      }

      toast.success(t('roles.details.saveSuccess'));
      navigate('/roles');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('roles.details.saveFail'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    Swal.fire({
      ...swalTheme(),
      title: t('common.are_you_sure'), text: t('roles.details.deleteConfirmText'),
      icon: 'warning', showCancelButton: true, confirmButtonColor: swalDangerColor(),
      confirmButtonText: t('roles.details.deleteBtn')
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`/api/roles/${id}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          toast.success(t('roles.details.deleteSuccess'));
          navigate('/roles');
        } catch (err) { toast.error(err.response?.data?.detail || t('roles.details.deleteFail')); }
      }
    });
  };

  useEffect(() => {
    const triggerDelete = () => handleDelete();
    window.addEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
    return () => window.removeEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
  }, [handleDelete]);

  return (
    <div className={`flex flex-col h-full bg-surface-app font-bold uppercase tracking-tighter text-[11px] ${isRTL ? 'font-cairo dir-rtl text-right' : 'text-left'}`} style={{ fontSize: `${fontSize}px` }}>
      <AuditHistoryModal isOpen={showAudit} onClose={() => setShowAudit(false)} tableName="roles" recordId={id} />
      {/* Header Toolbar */}
      <div className="bg-surface-card p-3 border-b border-line shadow-sm flex justify-between items-center sticky top-0 z-20">
        <div className={`flex items-center gap-4 ${isRTL ? 'mr-4' : 'ml-4'}`}>
          <button onClick={() => navigate('/roles')} className="p-1 hover:bg-surface-panel rounded-full text-primary transition-colors">
            <ArrowLeft size={18} className={isRTL ? 'rotate-180' : ''} />
          </button>
          <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`}>
            <span className="text-[9px] text-ink-secondary italic font-black">{t('roles.details.configLabel')}</span>
            <span className="text-primary font-black text-lg leading-tight">
                {roleData.name || t('roles.details.newRole')}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && isSuperAdmin && (
            <button
              onClick={handleDelete}
              className="p-2.5 text-status-danger hover:bg-status-danger-bg rounded-sm transition-colors"
              title={t('roles.details.deleteRole')}
            >
              <Trash2 size={16}/>
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={loading || !isSuperAdmin}
            title={!isSuperAdmin ? t('roles.details.onlySuperAdmin') : undefined}
            className="bg-primary text-white px-10 py-2.5 rounded-sm flex items-center gap-2 hover:bg-primary-dark transition-all shadow-lg active:scale-95 disabled:opacity-50"
          >
            <Save size={16} /> {loading ? t('roles.details.processing') : t('roles.details.saveRole')}
          </button>
        </div>
      </div>

      {/* 🎯 التابات: إعدادات الجروب + اليوزرات المربوطة بيه (زي Maximo) */}
      <div className="flex bg-surface-card border-b border-line px-4">
        {[
          { key: 'group', label: t('roleUsers.tabGroup', 'Security Group'), icon: <Shield size={13}/> },
          { key: 'users', label: t('roleUsers.tabUsers', 'Users'), icon: <Users size={13}/> },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`px-6 py-2.5 flex items-center gap-2 text-[10px] font-black uppercase tracking-tighter transition-all
              ${activeTab === tab.key ? 'border-b-2 border-primary-dark text-primary-dark' : 'text-ink-secondary hover:text-ink'}`}>
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'users' ? (
        <div className="flex-1 overflow-auto p-8 max-w-6xl mx-auto w-full">
          {isNew
            ? <div className="p-10 text-center text-ink-secondary italic normal-case">{t('roleUsers.saveFirst', 'Save the group first to see its users')}</div>
            : <RoleUsersTab roleId={id} isRTL={isRTL} />}
        </div>
      ) : (
      <div className="flex-1 overflow-auto p-8 max-w-6xl mx-auto w-full space-y-8">
        {/* Role Name Input */}
        <div className="bg-surface-card border-2 border-line p-6 rounded-sm shadow-sm">
          <label className="text-[9px] text-ink-secondary block mb-1 tracking-widest">{t('roles.details.roleIdentifier')}</label>
          <input
            className={`w-full border-b-2 border-primary outline-none p-2 text-xl font-black text-primary uppercase placeholder-ink-secondary/40 ${isRTL ? 'text-right' : 'text-left'}`}
            placeholder={t('roles.details.namePlaceholder')}
            value={roleData.name}
            onChange={(e) => setRoleData({...roleData, name: e.target.value.toUpperCase()})}
          />
        </div>

        {/* 🎯 Sites - زي تابة Sites في Security Groups بتاعة Maximo */}
        <div className="bg-surface-card border-2 border-line rounded-sm shadow-sm overflow-hidden">
          <div className="bg-primary-dark text-white p-3 text-[10px] tracking-widest flex items-center justify-between font-black border-b border-white/10">
            <span className="flex items-center gap-2 text-sky"><MapPin size={14} className="text-status-warning"/> {t('siteAccess.title', 'Sites')}</span>
            <label className="flex items-center gap-2 cursor-pointer normal-case">
              <input type="checkbox" className="w-4 h-4 accent-primary" checked={roleData.all_sites}
                onChange={e => setRoleData({ ...roleData, all_sites: e.target.checked })} />
              {t('siteAccess.allSites', 'Authorize Group for All Sites')}
            </label>
          </div>
          {roleData.all_sites ? (
            <div className="p-4 text-[10px] text-status-success normal-case italic">{t('siteAccess.allSitesHint', 'Users in this group can access every site.')}</div>
          ) : (
            <>
              <table className={`w-full ${isRTL ? 'text-right' : 'text-left'}`}>
                <thead className="bg-surface-panel text-[9px] text-ink-secondary border-b border-line">
                  <tr>
                    <th className="p-3 tracking-widest">{t('siteAccess.site', 'Site')}</th>
                    <th className="p-3 tracking-widest">{t('siteAccess.description', 'Description')}</th>
                    <th className="p-3 tracking-widest">{t('siteAccess.org', 'Organization')}</th>
                    <th className="p-3 w-12"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line-subtle">
                  {roleData.sites.length === 0 && (
                    <tr><td colSpan="4" className="p-4 text-center text-ink-secondary italic normal-case">{t('siteAccess.noSites', 'No sites authorized')}</td></tr>
                  )}
                  {roleData.sites.map(s => (
                    <tr key={s.site_id} className="hover:bg-primary-soft/30">
                      <td className="p-3 font-black text-primary">{s.site_id}</td>
                      <td className="p-3 normal-case">{s.description}</td>
                      <td className="p-3">{s.org_id}</td>
                      <td className="p-3 text-center">
                        <button onClick={() => setRoleData({ ...roleData, sites: roleData.sites.filter(x => x.site_id !== s.site_id) })}
                          className="text-status-danger hover:bg-status-danger-bg p-1 rounded-sm"><Trash2 size={14}/></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="p-3 border-t border-line bg-surface-panel">
                <select value="" onChange={e => {
                    const site = allSites.find(s => s.site_id === e.target.value);
                    if (site) setRoleData({ ...roleData, sites: [...roleData.sites, site] });
                  }}
                  className="bg-surface-card border border-line rounded-sm px-2 py-1 text-primary font-black outline-none">
                  <option value="">+ {t('siteAccess.addSite', 'Add site...')}</option>
                  {availableSites.map(s => <option key={s.site_id} value={s.site_id}>{s.site_id} - {s.description} ({s.org_id})</option>)}
                </select>
              </div>
            </>
          )}
        </div>

        {/* Permissions Groups */}
        {allModuleGroups.map((group, gIdx) => (
          <div key={gIdx} className="bg-surface-card border-2 border-line rounded-sm shadow-sm overflow-hidden">
            <div className="bg-primary-dark text-white p-3 text-[10px] tracking-widest flex items-center justify-between font-black border-b border-white/10">
              <span className="flex items-center gap-2 text-sky">
                <Shield size={14} className="text-status-warning"/> {t(group.groupKey, group.group)}
              </span>
              <div className="flex gap-4 items-center">
                  <span className={`text-[8px] text-white/60 italic ${isRTL ? 'ml-2' : 'mr-2'}`}>{t('roles.details.quickToggle')}</span>
                  {['read', 'create', 'update', 'delete'].map(action => (
                    <button
                      key={action}
                      onClick={() => toggleGroupAction(group.modules, action)}
                      className="hover:text-status-warning transition-colors cursor-pointer border border-white/20 px-1.5 py-0.5 rounded uppercase text-[8px]"
                    >
                      {t('roles.details.quickAllPrefix')} {t(`roles.details.actions.${action}`)}
                    </button>
                  ))}
              </div>
            </div>
            <table className={`w-full ${isRTL ? 'text-right' : 'text-left'}`}>
              <thead className="bg-surface-panel text-[9px] text-ink-secondary border-b border-line">
                <tr>
                  <th className="p-4 w-2/5 tracking-widest">{t('roles.details.moduleCol')}</th>
                  {['read', 'create', 'update', 'delete'].map(a => (
                    <th key={a} className="p-4 text-center border-l border-line w-20 tracking-widest">{t(`roles.details.actions.${a}`)}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-line-subtle">
                {group.modules.map((mod) => (
                  <tr key={mod.id} className="hover:bg-primary-soft/30 group transition-colors">
                    <td className="p-4 flex items-center gap-3 font-black text-primary">
                      <mod.icon size={16} className="text-line group-hover:text-primary transition-colors" />
                      {mod.labelKey ? t(mod.labelKey, mod.label) : mod.label}
                    </td>
                    {['read', 'create', 'update', 'delete'].map(action => (
                      <td key={action} className="p-4 text-center border-l border-line-subtle">
                        <input
                          type="checkbox"
                          className="w-4 h-4 accent-primary cursor-pointer shadow-sm"
                          checked={roleData.permissions?.[mod.id]?.[action] || false}
                          onChange={() => handlePermissionChange(mod.id, action)}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
      )}
    </div>
  );
};

const USERS_PAGE_SIZE = 10;

// 🎯 تابة اليوزرات: كل يوزر مربوط بالجروب كرول أساسي أو إضافي - بحث وباجينيشن
// من السيرفر، والضغط على اليوزر بيفتح شاشته
const RoleUsersTab = ({ roleId, isRTL }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);

  // البحث بيتبعت بعد ما اليوزر يوقف كتابة شوية بدل طلب مع كل حرف
  useEffect(() => {
    const timer = setTimeout(() => { setQuery(search.trim()); setPage(1); }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/roles/${roleId}/users`, { params: { page, limit: USERS_PAGE_SIZE, q: query || undefined } })
      .then(res => { if (alive) { setRows(res.data.data || []); setTotal(res.data.total || 0); } })
      .catch(err => { if (alive) toast.error(err.response?.data?.detail || t('roles.details.loadFail')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [roleId, page, query, t]);

  const pages = Math.max(1, Math.ceil(total / USERS_PAGE_SIZE));
  const from = total ? (page - 1) * USERS_PAGE_SIZE + 1 : 0;
  const to = Math.min(page * USERS_PAGE_SIZE, total);
  const pagerBtn = "p-1 rounded-sm border border-line hover:bg-primary-soft hover:text-primary disabled:opacity-30";

  return (
    <div className="bg-surface-card border-2 border-line rounded-sm shadow-sm overflow-hidden">
      <div className="bg-primary-dark text-white p-3 text-[10px] tracking-widest flex items-center justify-between gap-4 font-black border-b border-white/10">
        <span className="flex items-center gap-2 text-sky"><Users size={14} className="text-status-warning"/> {t('roleUsers.tabUsers', 'Users')} ({total})</span>
        <div className="flex items-center gap-2 bg-white/10 rounded-sm px-2 py-1">
          <Search size={12} className="text-white/60" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('roleUsers.search', 'Search users...')}
            className="bg-transparent outline-none text-white placeholder-white/50 normal-case w-56" />
        </div>
      </div>
      <table className={`w-full ${isRTL ? 'text-right' : 'text-left'}`}>
        <thead className="bg-surface-panel text-[9px] text-ink-secondary border-b border-line">
          <tr>
            <th className="p-3 tracking-widest">{t('roleUsers.username', 'Username')}</th>
            <th className="p-3 tracking-widest">{t('roleUsers.displayName', 'Name')}</th>
            <th className="p-3 tracking-widest">{t('roleUsers.email', 'Email')}</th>
            <th className="p-3 tracking-widest">{t('roleUsers.org', 'Organization')}</th>
            <th className="p-3 tracking-widest">{t('roleUsers.site', 'Default Site')}</th>
            <th className="p-3 tracking-widest text-center">{t('roleUsers.assignment', 'Assignment')}</th>
            <th className="p-3 tracking-widest text-center">{t('roleUsers.status', 'Status')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line-subtle">
          {!loading && rows.length === 0 && (
            <tr><td colSpan="7" className="p-8 text-center text-ink-secondary italic normal-case">{t('roleUsers.none', 'No users are assigned to this group')}</td></tr>
          )}
          {rows.map(u => (
            <tr key={u.user_id} onClick={() => navigate(`/users/${u.user_id}`)} className="hover:bg-primary-soft/30 cursor-pointer">
              <td className="p-3 font-black text-primary">{u.username}</td>
              <td className="p-3 normal-case">{u.display_name || '---'}</td>
              <td className="p-3 normal-case text-ink-secondary">{u.email || '---'}</td>
              <td className="p-3">{u.org_id || '---'}</td>
              <td className="p-3">{u.site_id || '---'}</td>
              <td className="p-3 text-center">
                <span className={`px-2 py-0.5 rounded-full text-[9px] ${u.assignment === 'PRIMARY' ? 'bg-primary-soft text-primary' : 'bg-surface-panel text-ink-secondary border border-line'}`}>
                  {u.assignment === 'PRIMARY' ? t('roleUsers.primary', 'Primary') : t('roleUsers.additional', 'Additional')}
                </span>
              </td>
              <td className="p-3 text-center">
                <span className={u.is_active ? 'text-status-success' : 'text-status-danger'}>
                  {u.is_active ? t('roleUsers.active', 'Active') : t('roleUsers.inactive', 'Inactive')}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {total > USERS_PAGE_SIZE && (
        <div className="flex items-center justify-end gap-2 p-3 border-t border-line bg-surface-panel text-[10px] font-black text-ink-secondary">
          <span className="font-mono" dir="ltr">{from}–{to} / {total}</span>
          <button type="button" className={pagerBtn} disabled={page <= 1} onClick={() => setPage(page - 1)}>
            <ChevronLeft size={14} className={isRTL ? 'rotate-180' : ''} />
          </button>
          <span className="font-mono" dir="ltr">{page} / {pages}</span>
          <button type="button" className={pagerBtn} disabled={page >= pages} onClick={() => setPage(page + 1)}>
            <ChevronRight size={14} className={isRTL ? 'rotate-180' : ''} />
          </button>
        </div>
      )}
    </div>
  );
};

export default RoleDetails;
